import { consumerClient } from "@/shared/services/apollo-clients/consumer-client";
import {
    GenericShopBasketAddItemDocument,
    GenericShopBasketAddItemMutation,
    GenericShopBasketAddItemMutationVariables,
    GenericShopBasketDocument,
    GenericShopBasketQuery,
    GenericShopBasketQueryVariables,
    ShopBasketAddItemInput,
} from "@/shared/services/graphql/generated/consumer-graph-types";

/** The basket as this slice selects it — items and the total, no identifiers. */
export type GenericShopBasketData = GenericShopBasketQuery["shopBasket"];

/**
 * Reads the basket of the authenticated user (QIT-7074).
 *
 * No `basketId` is passed or held: the shop service resolves the basket from
 * the session.
 *
 * Queried imperatively through `consumerClient` rather than with the generated
 * lazy-query composable, which only disposes its subscription and watchers
 * inside a component instance — this runs from a plain async function, and the
 * cart menu polls it every 10 seconds, so a composable would leak one
 * subscription per poll for as long as the tab stays open.
 *
 * `fetchPolicy` is pinned for that same poll: a cached answer would freeze the
 * badge after the first read. `consumerClient` defaults to `network-only`
 * already, but that default is expected to change (tech issue 0009 / QIT-7029).
 *
 * A failure yields `null` rather than an exception — the badge is ambient
 * information and the next poll will try again. Compare
 * {@link addGenericShopBasketItem}, where the user is waiting on the result.
 */
export const loadGenericShopBasket = async (): Promise<GenericShopBasketData | null> => {
    try {
        const { data } = await consumerClient.query<GenericShopBasketQuery, GenericShopBasketQueryVariables>({
            query: GenericShopBasketDocument,
            fetchPolicy: "network-only",
        });

        return data?.shopBasket ?? null;
    } catch (error) {
        console.warn("Generic shop: basket could not be read.", error);
        return null;
    }
};

/**
 * Adds an article to the basket and returns the basket as it now stands
 * (QIT-7074).
 *
 * Failures propagate. The widget shows an explicit "could not be added"
 * notification and keeps the button disabled, so swallowing the error here
 * would tell the user their article was added when it was not.
 */
export const addGenericShopBasketItem = async (item: ShopBasketAddItemInput): Promise<GenericShopBasketData> => {
    const result = await consumerClient.mutate<
        GenericShopBasketAddItemMutation,
        GenericShopBasketAddItemMutationVariables
    >({
        mutation: GenericShopBasketAddItemDocument,
        variables: { item },
    });

    if (!result.data?.shopBasketAddItem) {
        throw new Error(`Generic shop: adding '${item.articleId}' to the basket returned no basket.`);
    }

    return result.data.shopBasketAddItem;
};
